import React, { useState } from 'react';
import { Input, Button, Dropdown, Label, Icon, Segment, Divider } from 'semantic-ui-react';
import { formatDateOnly } from '../utils/timezone.utils';

/**
 * Sidebar Filter Panel - Slides in from the right
 * Doesn't take vertical space, keeps table fully visible
 */
const AdvancedFilterPanel = ({
    // Basic filters
    statusFilter,
    cloudNameFilter,
    categoryFilter,
    requestIdFilter,
    // Advanced filters
    globalSearch,
    selectedStatuses,
    selectedCategories,
    dateRangeStart,
    dateRangeEnd,
    submittedByFilter,
    // Saved filters
    savedFilters = [],
    filterPresetName,
    availableStatuses = [],
    availableCategories = [],
    // Callbacks
    onStatusFilterChange,
    onCloudNameFilterChange,
    onCategoryFilterChange,
    onRequestIdFilterChange,
    onGlobalSearchChange,
    onSelectedStatusesChange,
    onSelectedCategoriesChange,
    onDateRangeStartChange,
    onDateRangeEndChange,
    onSubmittedByFilterChange,
    onFilterPresetNameChange,
    onSaveFilterPreset,
    onLoadFilterPreset,
    onDeleteFilterPreset,
    onResetFilters,
    hasActiveFilters,
    onFilterChange,
    onClose // Callback to close the sidebar
}) => {
    const [activeSection, setActiveSection] = useState('search'); // 'search', 'filters', 'saved'

    const handleFilterChange = (setter, value) => {
        if (setter) {
            setter(value);
        }
        if (onFilterChange) {
            onFilterChange();
        }
    };

    const handleMultiSelectChange = (setter, value) => {
        if (setter) {
            setter(value);
        }
        if (onFilterChange) {
            onFilterChange();
        }
    };

    // Status dropdown options
    const statusOptions = availableStatuses.map(status => ({
        key: status,
        text: status,
        value: status
    }));

    // Category dropdown options
    const categoryOptions = availableCategories.map(category => ({
        key: category,
        text: category,
        value: category
    }));

    const activeFilterCount = [
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        globalSearch,
        selectedStatuses.length,
        selectedCategories.length,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter
    ].filter(v => v && v !== 0).length;

    return (
        <>
            {/* Overlay */}
            {onClose && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        zIndex: 999,
                        transition: 'opacity 0.3s ease',
                        cursor: 'pointer'
                    }}
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <div style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                width: '420px',
                maxWidth: '90vw',
                backgroundColor: '#ffffff',
                boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                animation: 'slideInRight 0.3s ease-out'
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px',
                    borderBottom: '2px solid #e5e7eb',
                    background: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                    }}>
                        <h3 style={{ 
                            margin: 0, 
                            fontSize: '1.3em', 
                            fontWeight: '600',
                            color: '#1b1c1d'
                        }}>
                            <Icon name="filter" color="blue" style={{ marginRight: '8px' }} />
                            Filters
                        </h3>
                        {onClose && (
                            <Button
                                icon="close"
                                circular
                                size="small"
                                onClick={onClose}
                                basic
                            />
                        )}
                    </div>
                    {hasActiveFilters && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 14px',
                            background: '#e3f2fd',
                            borderRadius: '8px',
                            fontSize: '0.9em',
                            border: '1px solid #90caf9',
                            color: '#062D4D',
                            fontWeight: '500'
                        }}>
                            <Icon name="check circle" color="blue" />
                            <span>{activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>

                {/* Navigation Tabs */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#f9fafb'
                }}>
                    {[
                        { key: 'search', icon: 'search', label: 'Search' },
                        { key: 'filters', icon: 'filter', label: 'Filters' },
                        { key: 'saved', icon: 'bookmark', label: `Saved${savedFilters.length > 0 ? ` (${savedFilters.length})` : ''}` }
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveSection(tab.key)}
                            style={{
                                flex: 1,
                                padding: '12px 8px',
                                border: 'none',
                                background: activeSection === tab.key ? '#ffffff' : 'transparent',
                                borderBottom: activeSection === tab.key ? '2px solid #062D4D' : '2px solid transparent',
                                color: activeSection === tab.key ? '#062D4D' : '#6b7280',
                                fontWeight: activeSection === tab.key ? '600' : '400',
                                fontSize: '0.85em',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <Icon name={tab.icon} color={activeSection === tab.key ? 'blue' : undefined} />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Scrollable Content */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '20px'
                }}>
                    {/* Search Section */}
                    {activeSection === 'search' && (
                        <div>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '0.9em',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    <Icon name="search" style={{ marginRight: '6px' }} />
                                    Global Search
                                </label>
                                <Input
                                    placeholder="Search all fields..."
                                    value={globalSearch || ''}
                                    onChange={(e) => handleFilterChange(onGlobalSearchChange, e.target.value)}
                                    icon="search"
                                    iconPosition="left"
                                    fluid
                                    size="large"
                                    className={globalSearch ? 'tb-filter-active' : ''}
                                    style={{
                                        borderColor: globalSearch ? '#062D4D' : undefined,
                                        boxShadow: globalSearch ? '0 0 0 1px #062D4D' : undefined
                                    }}
                                />
                                <div style={{
                                    marginTop: '8px',
                                    fontSize: '0.8em',
                                    color: '#6b7280',
                                    fontStyle: 'italic'
                                }}>
                                    Searches across ID, name, category, status, email, dates, and comments
                                </div>
                            </div>

                            <Divider />

                            <div style={{ marginTop: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '12px',
                                    fontSize: '0.9em',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    Quick Filters
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div>
                                        <Input
                                            placeholder="Request ID"
                                            value={requestIdFilter || ''}
                                            onChange={(e) => handleFilterChange(onRequestIdFilterChange, e.target.value)}
                                            icon="hashtag"
                                            iconPosition="left"
                                            fluid
                                            className={requestIdFilter ? 'tb-filter-active' : ''}
                                            style={{
                                                borderColor: requestIdFilter ? '#062D4D' : undefined,
                                                boxShadow: requestIdFilter ? '0 0 0 1px #062D4D' : undefined
                                            }}
                                        />
                                        {requestIdFilter && (
                                            <Label size="tiny" color="blue" style={{ marginTop: '4px', display: 'inline-block' }}>
                                                Active
                                            </Label>
                                        )}
                                    </div>
                                    <div>
                                        <Input
                                            placeholder="Cloud Name"
                                            value={cloudNameFilter || ''}
                                            onChange={(e) => handleFilterChange(onCloudNameFilterChange, e.target.value)}
                                            icon="cloud"
                                            iconPosition="left"
                                            fluid
                                            className={cloudNameFilter ? 'tb-filter-active' : ''}
                                            style={{
                                                borderColor: cloudNameFilter ? '#062D4D' : undefined,
                                                boxShadow: cloudNameFilter ? '0 0 0 1px #062D4D' : undefined
                                            }}
                                        />
                                        {cloudNameFilter && (
                                            <Label size="tiny" color="blue" style={{ marginTop: '4px', display: 'inline-block' }}>
                                                Active
                                            </Label>
                                        )}
                                    </div>
                                    <div>
                                        <Input
                                            placeholder="Submitted By (Email)"
                                            value={submittedByFilter || ''}
                                            onChange={(e) => handleFilterChange(onSubmittedByFilterChange, e.target.value)}
                                            icon="user"
                                            iconPosition="left"
                                            fluid
                                            className={submittedByFilter ? 'tb-filter-active' : ''}
                                            style={{
                                                borderColor: submittedByFilter ? '#062D4D' : undefined,
                                                boxShadow: submittedByFilter ? '0 0 0 1px #062D4D' : undefined
                                            }}
                                        />
                                        {submittedByFilter && (
                                            <Label size="tiny" color="blue" style={{ marginTop: '4px', display: 'inline-block' }}>
                                                Active
                                            </Label>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filters Section */}
                    {activeSection === 'filters' && (
                        <div>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '0.9em',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    <Icon name="tags" style={{ marginRight: '6px' }} />
                                    Status (Multi-select)
                                </label>
                                <Dropdown
                                    placeholder="Select statuses..."
                                    fluid
                                    multiple
                                    selection
                                    search
                                    options={statusOptions}
                                    value={selectedStatuses}
                                    onChange={(e, { value }) => handleMultiSelectChange(onSelectedStatusesChange, value)}
                                />
                                {selectedStatuses.length > 0 && (
                                    <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {selectedStatuses.map(status => (
                                            <Label key={status} color="teal">
                                                {status}
                                                <Icon
                                                    name="delete"
                                                    onClick={() => {
                                                        const updated = selectedStatuses.filter(s => s !== status);
                                                        handleMultiSelectChange(onSelectedStatusesChange, updated);
                                                    }}
                                                    style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                />
                                            </Label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Divider />

                            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    fontSize: '0.9em',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    <Icon name="folder" color={selectedCategories.length > 0 ? 'blue' : undefined} style={{ marginRight: '6px' }} />
                                    Category (Multi-select)
                                    {selectedCategories.length > 0 && (
                                        <Label size="tiny" color="blue" circular style={{ marginLeft: '8px' }}>
                                            {selectedCategories.length}
                                        </Label>
                                    )}
                                </label>
                                <Dropdown
                                    placeholder="Select categories..."
                                    fluid
                                    multiple
                                    selection
                                    search
                                    options={categoryOptions}
                                    value={selectedCategories || []}
                                    onChange={(e, { value }) => handleMultiSelectChange(onSelectedCategoriesChange, value)}
                                    className={selectedCategories.length > 0 ? 'tb-filter-active' : ''}
                                    style={{
                                        borderColor: selectedCategories.length > 0 ? '#062D4D' : undefined,
                                        boxShadow: selectedCategories.length > 0 ? '0 0 0 1px #062D4D' : undefined
                                    }}
                                />
                                {selectedCategories.length > 0 && (
                                    <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {selectedCategories.map(category => (
                                            <Label key={category} color="blue">
                                                {category}
                                                <Icon
                                                    name="delete"
                                                    onClick={() => {
                                                        const updated = selectedCategories.filter(c => c !== category);
                                                        handleMultiSelectChange(onSelectedCategoriesChange, updated);
                                                    }}
                                                    style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                />
                                            </Label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Divider />

                            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    fontSize: '0.9em',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    <Icon name="calendar" color={(dateRangeStart || dateRangeEnd) ? 'blue' : undefined} style={{ marginRight: '6px' }} />
                                    Date Range
                                    {(dateRangeStart || dateRangeEnd) && (
                                        <Label size="tiny" color="blue" circular style={{ marginLeft: '8px' }}>
                                            Active
                                        </Label>
                                    )}
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            fontSize: '0.85em',
                                            color: '#6b7280'
                                        }}>
                                            From
                                        </label>
                                        <Input
                                            type="date"
                                            value={dateRangeStart || ''}
                                            onChange={(e) => handleFilterChange(onDateRangeStartChange, e.target.value)}
                                            fluid
                                            className={dateRangeStart ? 'tb-filter-active' : ''}
                                            style={{
                                                borderColor: dateRangeStart ? '#062D4D' : undefined,
                                                boxShadow: dateRangeStart ? '0 0 0 1px #062D4D' : undefined
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            fontSize: '0.85em',
                                            color: '#6b7280'
                                        }}>
                                            To
                                        </label>
                                        <Input
                                            type="date"
                                            value={dateRangeEnd || ''}
                                            onChange={(e) => handleFilterChange(onDateRangeEndChange, e.target.value)}
                                            fluid
                                            className={dateRangeEnd ? 'tb-filter-active' : ''}
                                            style={{
                                                borderColor: dateRangeEnd ? '#062D4D' : undefined,
                                                boxShadow: dateRangeEnd ? '0 0 0 1px #062D4D' : undefined
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <Divider />

                            <div style={{ marginTop: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '0.9em',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}>
                                    Text Search Filters
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <Input
                                        placeholder="Status text search..."
                                        value={statusFilter}
                                        onChange={(e) => handleFilterChange(onStatusFilterChange, e.target.value)}
                                        icon="search"
                                        iconPosition="left"
                                        fluid
                                    />
                                    <Input
                                        placeholder="Category text search..."
                                        value={categoryFilter || ''}
                                        onChange={(e) => handleFilterChange(onCategoryFilterChange, e.target.value)}
                                        icon="search"
                                        iconPosition="left"
                                        fluid
                                        className={categoryFilter ? 'tb-filter-active' : ''}
                                        style={{
                                            borderColor: categoryFilter ? '#062D4D' : undefined,
                                            boxShadow: categoryFilter ? '0 0 0 1px #062D4D' : undefined
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Saved Filters Section */}
                    {activeSection === 'saved' && (
                        <div>
                            {savedFilters.length === 0 ? (
                                <div style={{
                                    padding: '40px 20px',
                                    textAlign: 'center',
                                    color: '#9ca3af'
                                }}>
                                    <Icon name="bookmark outline" size="huge" style={{ marginBottom: '12px', opacity: 0.5 }} />
                                    <p style={{ margin: 0, fontSize: '0.95em', fontWeight: '500' }}>No saved filters</p>
                                    <p style={{ margin: '8px 0 0 0', fontSize: '0.85em' }}>Save your filter combinations for quick access</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {savedFilters.map(preset => (
                                        <div
                                            key={preset.id}
                                            style={{
                                                padding: '14px',
                                                background: '#f9fafb',
                                                borderRadius: '8px',
                                                border: '1px solid #e5e7eb',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = '#f3f4f6';
                                                e.currentTarget.style.borderColor = '#d1d5db';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#f9fafb';
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '8px'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontWeight: '600',
                                                        fontSize: '0.95em',
                                                        color: '#062D4D',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {preset.name}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.8em',
                                                        color: '#6b7280'
                                                    }}>
                                                        {formatDateOnly(preset.createdAt)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                                <Button
                                                    size="small"
                                                    icon="play"
                                                    content="Load"
                                                    onClick={() => onLoadFilterPreset && onLoadFilterPreset(preset)}
                                                    positive
                                                    fluid
                                                />
                                                <Button
                                                    size="small"
                                                    negative
                                                    icon="trash"
                                                    onClick={() => onDeleteFilterPreset && onDeleteFilterPreset(preset.id)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '16px 20px',
                    borderTop: '1px solid #e5e7eb',
                    background: '#f9fafb'
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Input
                                placeholder="Save filters as..."
                                value={filterPresetName}
                                onChange={(e) => onFilterPresetNameChange && onFilterPresetNameChange(e.target.value)}
                                size="small"
                                style={{ flex: 1 }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && filterPresetName.trim() && onSaveFilterPreset) {
                                        onSaveFilterPreset();
                                    }
                                }}
                            />
                            <Button
                                icon="save"
                                onClick={onSaveFilterPreset}
                                disabled={!filterPresetName.trim()}
                                positive
                                size="small"
                            />
                        </div>
                        <Button
                            fluid
                            negative
                            icon="remove"
                            content="Clear All Filters"
                            onClick={() => {
                                if (onResetFilters) onResetFilters();
                                if (onFilterChange) onFilterChange();
                            }}
                            disabled={!hasActiveFilters}
                        />
                    </div>
                </div>
            </div>

            {/* CSS Animation */}
            <style>{`
                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                    }
                    to {
                        transform: translateX(0);
                    }
                }
            `}</style>
        </>
    );
};

export default AdvancedFilterPanel;
